export const DEFAULT_COOKING_FILTERS = Object.freeze({
  maxTime: "all",
  difficulty: "all",
  mainType: "all",
  seasonalOnly: false,
  excludedText: "",
  excludedAllergens: [],
});

const FILTER_STORAGE_PREFIX = "aile-cooking-filters:v1";

const normalize = (value, lang = "tr") => String(value || "")
  .trim()
  .toLocaleLowerCase(lang === "en" ? "en-US" : "tr-TR")
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "");

const includesAny = (text, terms) => terms.some((term) => text.includes(term));

const TYPE_TERMS = {
  tr: {
    fish: ["balık", "hamsi", "palamut", "levrek", "çipura", "alabalık", "somon", "uskumru"],
    chicken: ["tavuk", "hindi"],
    meat: ["köfte", "kıyma", "kıymalı", "etli", "et sote", "kebap", "kebab", "kuzu", "dana", "kavurma", "tandır", "lahmacun", "ali nazik", "hünkâr", "hünkar", "çökertme", "sucuk"],
  },
  en: {
    fish: ["fish", "salmon", "cod", "tilapia", "tuna", "shrimp", "seafood", "clam"],
    chicken: ["chicken", "turkey", "duck"],
    meat: ["beef", "steak", "pork", "ham", "bacon", "sausage", "meatball", "meatloaf", "burger", "lamb", "shepherd", "cottage pie", "bolognese", "bratwurst", "kielbasa", "reuben", "bangers", "sloppy joe", "cheesesteak"],
  },
};

const SEASON_TERMS = {
  winter: ["pırasa", "kereviz", "karnabahar", "lahana", "ıspanak", "broccoli", "cauliflower", "leek", "cabbage", "parsnip", "brussels sprout"],
  spring: ["enginar", "bakla", "bezelye", "semizotu", "asparagus", "artichoke", "spring pea", "watercress"],
  summer: ["bamya", "taze fasulye", "patlıcan", "kabak", "domates", "biber", "okra", "green bean", "aubergine", "eggplant", "zucchini", "courgette", "tomato", "sweetcorn", "corn on the cob"],
  autumn: ["balkabağı", "mantar", "kestane", "ayva", "pumpkin", "butternut", "mushroom", "squash", "apple pork"],
};

const DIFFICULTY_RANK = { easy: 1, medium: 2, hard: 3 };

function safeStorage(storage) {
  if (storage) return storage;
  if (typeof window === "undefined") return null;
  try { return window.localStorage; } catch (_) { return null; }
}

function filterStorageKey(familyId, lang) {
  return `${FILTER_STORAGE_PREFIX}:${encodeURIComponent(String(familyId || "anonymous"))}:${lang === "en" ? "en" : "tr"}`;
}

export function normalizeCookingFilters(filters = {}) {
  const maxTime = ["all", "30", "45", "60"].includes(String(filters.maxTime)) ? String(filters.maxTime) : "all";
  const difficulty = ["all", "easy", "medium"].includes(filters.difficulty) ? filters.difficulty : "all";
  const mainType = ["all", "meat", "chicken", "fish", "vegetarian"].includes(filters.mainType) ? filters.mainType : "all";
  return {
    maxTime,
    difficulty,
    mainType,
    seasonalOnly: filters.seasonalOnly === true,
    excludedText: String(filters.excludedText || "").slice(0, 120),
    excludedAllergens: Array.isArray(filters.excludedAllergens)
      ? [...new Set(filters.excludedAllergens.map(String).filter(Boolean))].slice(0, 12)
      : [],
  };
}

export function readCookingFilters({ familyId, lang = "tr", storage } = {}) {
  const target = safeStorage(storage);
  if (!target || !familyId) return { ...DEFAULT_COOKING_FILTERS };
  try {
    const value = JSON.parse(target.getItem(filterStorageKey(familyId, lang)) || "null");
    return normalizeCookingFilters(value || DEFAULT_COOKING_FILTERS);
  } catch (_) {
    return { ...DEFAULT_COOKING_FILTERS };
  }
}

export function writeCookingFilters({ familyId, lang = "tr", filters, storage } = {}) {
  const target = safeStorage(storage);
  if (!target || !familyId) return false;
  try {
    target.setItem(filterStorageKey(familyId, lang), JSON.stringify(normalizeCookingFilters(filters)));
    return true;
  } catch (_) {
    return false;
  }
}

export function getCurrentSeasonKey(date = new Date()) {
  const month = date.getMonth() + 1;
  if (month === 12 || month <= 2) return "winter";
  if (month <= 5) return "spring";
  if (month <= 8) return "summer";
  return "autumn";
}

export function parseRecipeMinutes(value) {
  const numbers = String(value || "").match(/\d+/g)?.map(Number).filter(Number.isFinite) || [];
  return numbers.length ? Math.max(...numbers) : null;
}

export function classifyMenuMain(menu, cookingData, lang = "tr") {
  const recipe = cookingData?.recipes?.[menu?.anaYemek] || {};
  const category = recipe.methodCategory;
  const name = normalize(menu?.anaYemek, lang);
  const terms = TYPE_TERMS[lang === "en" ? "en" : "tr"];

  if (category === "fish" || includesAny(name, terms.fish.map((term) => normalize(term, lang)))) return "fish";
  if (category === "poultry" || includesAny(name, terms.chicken.map((term) => normalize(term, lang)))) return "chicken";
  if (["meat", "groundMeat"].includes(category) || includesAny(name, terms.meat.map((term) => normalize(term, lang)))) return "meat";
  return "vegetarian";
}

function menuRecipes(menu, cookingData) {
  return [menu?.anaYemek, menu?.corba, menu?.salata]
    .map((name) => cookingData?.recipes?.[name])
    .filter(Boolean);
}

function isSeasonCompatible(menu, lang, season) {
  const dishText = normalize([menu?.anaYemek, menu?.corba, menu?.salata].join(" "), lang);
  const matchedSeasons = Object.entries(SEASON_TERMS)
    .filter(([, terms]) => includesAny(dishText, terms.map((term) => normalize(term, lang))))
    .map(([key]) => key);
  return matchedSeasons.length === 0 || matchedSeasons.includes(season);
}

export function getAvailableAllergens(cookingData) {
  return [...new Set(Object.values(cookingData?.recipes || {})
    .flatMap((recipe) => Array.isArray(recipe?.allergens) ? recipe.allergens : [])
    .map(String)
    .filter(Boolean))]
    .sort((a, b) => a.localeCompare(b));
}

export function getMenuPersonalizationMeta(menu, cookingData, lang = "tr", date = new Date()) {
  const recipes = menuRecipes(menu, cookingData);
  const mainRecipe = cookingData?.recipes?.[menu?.anaYemek] || {};
  const mainMinutes = parseRecipeMinutes(mainRecipe.totalTime || mainRecipe.sure);
  const difficultyRank = DIFFICULTY_RANK[mainRecipe.difficultyKey] || 2;
  const allergens = [...new Set(recipes.flatMap((recipe) => recipe.allergens || []))];
  const searchText = normalize([
    menu?.anaYemek,
    menu?.corba,
    menu?.salata,
    ...recipes.flatMap((recipe) => recipe.malzemeler || []),
  ].join(" "), lang);

  return {
    maxMinutes: mainMinutes,
    difficultyRank,
    mainType: classifyMenuMain(menu, cookingData, lang),
    allergens,
    searchText,
    seasonCompatible: isSeasonCompatible(menu, lang, getCurrentSeasonKey(date)),
  };
}

export function filterCookingMenus(menus = [], cookingData, rawFilters = {}, lang = "tr", date = new Date()) {
  const filters = normalizeCookingFilters(rawFilters);
  const maxTime = filters.maxTime === "all" ? null : Number(filters.maxTime);
  const maxDifficulty = filters.difficulty === "all" ? null : DIFFICULTY_RANK[filters.difficulty];
  const excludedTokens = filters.excludedText
    .split(/[,;\n]+/)
    .map((value) => normalize(value, lang))
    .filter((value) => value.length >= 2);
  const excludedAllergens = new Set(filters.excludedAllergens);

  return menus.filter((menu) => {
    const meta = getMenuPersonalizationMeta(menu, cookingData, lang, date);
    if (maxTime && (!meta.maxMinutes || meta.maxMinutes > maxTime)) return false;
    if (maxDifficulty && meta.difficultyRank > maxDifficulty) return false;
    if (filters.mainType !== "all" && meta.mainType !== filters.mainType) return false;
    if (filters.seasonalOnly && !meta.seasonCompatible) return false;
    if (excludedTokens.some((token) => meta.searchText.includes(token))) return false;
    if (meta.allergens.some((allergen) => excludedAllergens.has(allergen))) return false;
    return true;
  });
}

export function hasActiveCookingFilters(rawFilters = {}) {
  const filters = normalizeCookingFilters(rawFilters);
  return filters.maxTime !== "all"
    || filters.difficulty !== "all"
    || filters.mainType !== "all"
    || filters.seasonalOnly
    || filters.excludedText.trim().length > 0
    || filters.excludedAllergens.length > 0;
}
